/**
 * Generic changes manager for tracking field modifications across entities
 * Supports any entity type with an 'id' field and configurable field mappings
 */
export interface EntityWithId {
  id: number;
}

export interface FieldMapper<TEntity extends EntityWithId, TFieldValues = Record<string, any>> {
  /**
   * Maps an entity to its field values for comparison
   */
  getFieldValue(entity: TEntity, field: keyof TFieldValues): string;

  /**
   * Gets all trackable fields from the entity
   */
  getTrackableFields(entity: TEntity): TFieldValues;
}

export class EntityChangesManager<
  TEntity extends EntityWithId,
  TFieldValues = Record<string, any>,
> {
  private changes: Map<number, TFieldValues> = new Map();
  private fieldMapper: FieldMapper<TEntity, TFieldValues>;

  constructor(fieldMapper: FieldMapper<TEntity, TFieldValues>) {
    this.fieldMapper = fieldMapper;
  }

  /**
   * Records a change for a specific entity field
   */
  setFieldChange<K extends keyof TFieldValues>(
    entityId: number,
    field: K,
    value: TFieldValues[K]
  ): void {
    const existingChanges = this.changes.get(entityId) || ({} as TFieldValues);
    this.changes.set(entityId, {
      ...existingChanges,
      [field]: value,
    });
  }

  /**
   * Gets the current value for an entity field from changed entities
   */
  getFieldValue<K extends keyof TFieldValues>(entity: TEntity, field: K): string {
    const changes = this.changes.get(entity.id);
    const changedValue = changes?.[field];

    if (changedValue !== undefined) {
      return String(changedValue);
    }

    return this.fieldMapper.getFieldValue(entity, field);
  }

  /**
   * Checks if an entity has any pending changes
   */
  hasChanges(entityId: number): boolean {
    const changes = this.changes.get(entityId);
    return Boolean(changes && Object.keys(changes).length > 0);
  }

  /**
   * Gets the changes for a specific entity
   */
  getChanges(entityId: number): TFieldValues | undefined {
    return this.changes.get(entityId);
  }

  /**
   * Gets all entity IDs that have changes
   */
  getChangedEntityIds(): number[] {
    return Array.from(this.changes.keys()).filter(id => this.hasChanges(id));
  }

  /**
   * Checks if an entity field has changed from its original value
   */
  hasFieldChanged<K extends keyof TFieldValues>(entity: TEntity, field: K): boolean {
    const changes = this.changes.get(entity.id);
    if (!changes || changes[field] === undefined) return false;

    const originalValue = this.fieldMapper.getFieldValue(entity, field);
    const changedValue = String(changes[field]);

    return changedValue !== originalValue;
  }

  /**
   * Removes all changes for a specific entity
   */
  clearEntityChanges(entityId: number): void {
    this.changes.delete(entityId);
  }

  /**
   * Removes all changes
   */
  clearAllChanges(): void {
    this.changes.clear();
  }

  /**
   * Gets the total number of entities with changes
   */
  getChangedEntitiesCount(): number {
    return this.getChangedEntityIds().length;
  }

  /**
   * Normalizes a value for comparison: treats null, undefined, and empty string as equivalent.
   */
  private normalizeForComparison(value: unknown): string {
    if (value === null || value === undefined || value === '') return '';
    return String(value);
  }

  /**
   * Detects and records changes from form values
   * Stores only the changed field values
   */
  detectChangesFromForm(entities: TEntity[], formValues: TFieldValues[]): void {
    entities.forEach((entity, index) => {
      const formValue = formValues[index];
      if (!formValue) return;

      const originalFields = this.fieldMapper.getTrackableFields(entity);
      const entityChanges: Partial<TFieldValues> = {};
      let hasChanges = false;

      Object.entries(formValue as Record<string, unknown>).forEach(([fieldName, fieldValue]) => {
        const originalValue = originalFields[fieldName as keyof TFieldValues];
        if (
          this.normalizeForComparison(fieldValue) !== this.normalizeForComparison(originalValue)
        ) {
          entityChanges[fieldName as keyof TFieldValues] =
            fieldValue as TFieldValues[keyof TFieldValues];
          hasChanges = true;
        }
      });

      if (hasChanges) {
        this.changes.set(entity.id, entityChanges as TFieldValues);
      } else {
        // Remove from changes if no longer has changes
        this.changes.delete(entity.id);
      }
    });
  }

  /**
   * Applies saved changes to form values using a setter function
   */
  applyChangesToForm(
    entities: TEntity[],
    setFormValue: (fieldPath: string, value: any) => void,
    formFieldPrefix: string = 'entities'
  ): void {
    entities.forEach((entity, index) => {
      const trackableFields = this.fieldMapper.getTrackableFields(entity);

      Object.keys(trackableFields as Record<string, unknown>).forEach(fieldName => {
        const fieldValue = this.getFieldValue(entity, fieldName as keyof TFieldValues);
        setFormValue(`${formFieldPrefix}.${index}.${fieldName}`, fieldValue);
      });
    });
  }

  /**
   * Iterates over all changes
   */
  forEach(callback: (entityId: number, changes: TFieldValues) => void): void {
    this.changes.forEach((changes, entityId) => {
      if (this.hasChanges(entityId)) {
        callback(entityId, changes);
      }
    });
  }

  /**
   * Handles entity deletion by removing its changes
   */
  clearEntitiesChanges(entityIds: number[]): void {
    entityIds.forEach(entityId => {
      this.clearEntityChanges(entityId);
    });
  }

  /**
   * Gets entities that have changes but are not in the current list (deleted/filtered out)
   */
  getOrphanedChanges(currentEntityIds: number[]): number[] {
    const currentIdSet = new Set(currentEntityIds);
    return this.getChangedEntityIds().filter(id => !currentIdSet.has(id));
  }

  /**
   * Gets all changes as a Map
   * Returns a map of entity ID to field changes
   */
  getAllChanges(): Map<number, TFieldValues> {
    const result = new Map<number, TFieldValues>();
    this.changes.forEach((changes, entityId) => {
      if (this.hasChanges(entityId)) {
        result.set(entityId, changes);
      }
    });
    return result;
  }
}
